import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateInvoiceIssuerMutation } from '../../api/account/accountApiSlice';
import { TenantInvoiceIssuer } from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

type InvoiceIssuerSectionProps = {
    readOnly: boolean;
    /** Stored value of the tenant IN VIEW — passed in, never re-queried
     *  (#1334). null = unconfigured; the invoice channel stays locked. */
    invoiceIssuer: TenantInvoiceIssuer | null;
    /** Master path: write the VIEWED tenant by slug (recording an existing
     *  customer's arrangement). Absent = the caller's own tenant. */
    masterSlug?: string;
};

/**
 * Who issues the statutory invoice — ADR-0012's two-mode toggle (WP0 under
 * #1629). It sits in Account with the other billing-gating facts (the
 * dividing rule): choosing it is what unlocks the "invoiced separately"
 * channel in Services.
 *
 * Both modes are choosable since 2026-08-19 (PO, Neuland staging review):
 * `external` (the tenant's own ERP issues — Mode C, InnoDom), and `kolula`
 * (Mode A) via its PRELIMINARY stage, which exists and is Neuland's live
 * practice — the invoice PDF comes from our system, the tenant records the
 * invoice number and payment receipt on the booking. The copy names it a
 * preliminary stage until the full machinery (gap-free numbering, immutable
 * archive, VAT snapshot) ships with the accounting stream.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern; PO
 * 2026-10-12): the view shows the stored choice, "Bearbeiten" opens a draft
 * of it. Removing the setting is part of editing — it empties the draft, and
 * saving the empty choice unsets it. The view is the stored value on every
 * render: the Account cache invalidation after a save re-renders it, so there
 * is no local mirror to drift.
 */
const InvoiceIssuerSection = ({ readOnly, invoiceIssuer, masterSlug }: InvoiceIssuerSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateInvoiceIssuer, { isLoading: saving }] = useUpdateInvoiceIssuerMutation();
    // Wrapped so the draft carries the choice as a field; null (unset) is a
    // real choice here. (The hook also handles a bare null draft now.)
    const saved = useMemo(() => ({ issuer: invoiceIssuer }), [invoiceIssuer]);
    const draft = useEditableDraft<{ issuer: TenantInvoiceIssuer | null }>(saved);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        setError(null);
        try {
            await updateInvoiceIssuer({ invoiceIssuer: draft.value.issuer, slug: masterSlug }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (e) {
            setError(errorMessageFrom(e, t('account.invoice_issuer_save_failed'), t));
        }
    };

    /** The choice — locked in the view, editable in the draft. */
    const renderChoice = (locked: boolean) => {
        const value = locked ? invoiceIssuer : draft.value.issuer;
        const option = (issuer: TenantInvoiceIssuer, label: string, hint: string) => (
            <label className={'flex items-start gap-2 text-sm text-foreground'}>
                <input
                    type={'radio'}
                    className={'mt-1'}
                    name={'invoice-issuer'}
                    value={issuer}
                    checked={value === issuer}
                    disabled={locked || saving}
                    onChange={() => draft.setDraft({ issuer })}
                />
                <span>
                    {label}
                    <span className={'block text-xs text-foreground/60'}>{hint}</span>
                </span>
            </label>
        );
        return (
            <div className={'flex flex-col gap-4'}>
                <div className={'flex flex-col gap-2'}>
                    {option(
                        'external',
                        t('account.invoice_issuer_external'),
                        t('account.invoice_issuer_external_hint'),
                    )}
                    {option(
                        'kolula',
                        t('account.invoice_issuer_kolula'),
                        t('account.invoice_issuer_kolula_hint'),
                    )}
                </div>
                {value === null && (
                    <p className={'text-xs text-foreground/60'}>{t('account.invoice_issuer_unset_hint')}</p>
                )}
                {!locked && value !== null && (
                    <div>
                        <CustomButton
                            title={t('account.invoice_issuer_clear')}
                            onClick={() => draft.setDraft({ issuer: null })}
                            isFilled={false}
                            disabled={saving}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <EditableSection
            id={'invoice-issuer'}
            title={t('account.invoice_issuer_section')}
            description={t('account.invoice_issuer_section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            error={error}
            onEdit={draft.startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void handleSave(); }}
            view={renderChoice(true)}
        >
            {renderChoice(false)}
        </EditableSection>
    );
};

export default InvoiceIssuerSection;
